import {
  DurationType,
  FrequencyType,
  OrderCancelType,
  OrderExpiration,
  PayerType,
} from "enums/Order";
import {
  IBalanceTestItem,
  ICreateBalanceOrderItem,
  ICreateGaitOrderItem,
  IGaitTestItem,
  IGrouppedCreateOrderItems,
  IOrderDetails,
  IOrderItem,
  IOrderVoidInfo,
} from "interfaces/Order";

import { Apps } from "enums/Common";
import { TestDurationValue } from "interfaces/Test";
import { TestType } from "enums/Test";
import { getTestDurationOptions } from "helpers/TestHelper";
import moment from "moment";

export const formatDuration = (duration: number): string => {
  let secs = duration;

  const hrs = Math.floor(secs / 3600);
  secs = secs - hrs * 3600;
  const mins = Math.floor(secs / 60);
  secs = secs - mins * 60;

  return `${hrs ? `${hrs} hours ` : ""} ${
    mins ? `${mins} ${mins > 1 ? "minutes" : "minute"} ` : ""
  } ${secs ? `${secs} seconds` : ""}`;
};

export const formatFrequency = ({
  frequency,
  frequencyReps,
}: {
  frequency: FrequencyType;
  frequencyReps: number;
}): string => {
  // Always display as "per week" - if Daily is passed (legacy data), it should have been converted already
  return `${frequencyReps} per week`;
};

export const isOrderScheduled = ({
  startDate,
}: {
  startDate: string;
}): boolean => {
  const currentTime = moment();
  const start = moment(startDate, "YYYY-MM-DD");

  // order is scheduled if start date is in the future
  return currentTime.isBefore(start, "day");
};

export const getOrderStatus = ({
  status,
  cancelType,
  startDate,
  endDate,
}: {
  status: number;
  cancelType?: OrderCancelType | null;
  startDate: string;
  endDate: string;
}): 'Canceled' | 'Voided' | 'Void Requested' | 'Scheduled' | 'Expired' | 'Active' => {
  if (cancelType === OrderCancelType.VoidRequested) return 'Void Requested';
  if (status === 2) // OrderStatus.Cancelled
    return cancelType === OrderCancelType.Voided ? 'Voided' : 'Canceled';
  if (isOrderScheduled({ startDate })) return 'Scheduled';
  if (hasOrderExpired({ startDate, endDate })) return 'Expired';
  return 'Active';
};

export const hasOrderExpired = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): boolean => {
  const currentTime = moment();
  const end = moment(endDate, "YYYY-MM-DD");

  // order is expired only if current date is after end date
  return currentTime.isAfter(end, "day");
};

export const formatGaitDataForAPI = (data: ICreateGaitOrderItem) => {
  const { tests, selected } = data;

  if (!selected) return undefined;

  return tests.map(({ duration, durationType, frequency, frequencyReps }) => ({
    duration: duration!,
    durationType: durationType ?? DurationType.Seconds,
    // Always send Weekly to API (Custom is UI-only)
    frequency: frequency === FrequencyType.Custom ? FrequencyType.Weekly : frequency!,
    frequencyReps: Number(frequencyReps),
  }));
};

export const formatBalanceDataForAPI = (data: ICreateBalanceOrderItem) => {
  const { selected, duration, durationType, frequency, frequencyReps, tests } =
    data;

  if (!selected || !frequency || !frequencyReps) return undefined;

  if (!frequency) return [];

  return tests
    .filter(({ params: { eyes } }) => eyes)
    .map(({ params: { feet, eyes } }) => ({
      duration,
      durationType,
      // Always send Weekly to API (Custom is UI-only)
      frequency: frequency === FrequencyType.Custom ? FrequencyType.Weekly : frequency,
      frequencyReps,
      params: { feet, eyes: eyes! },
    }));
};

// export const formatAnglesSideAndParamsForAPI = ({
//   leftSide,
//   rightSide,
//   params,
// }: {
//   leftSide?: boolean;
//   rightSide?: boolean;
//   params: IAnglesParam[];
// }): {
//   side: AnglesLocation;
//   params: { region: AnglesRegion; motion: AnglesMotion; reps: number }[];
// } => {
//   let side: AnglesLocation;
//   const apiParams: {
//     region: AnglesRegion;
//     motion: AnglesMotion;
//     reps: number;
//   }[] = [];

//   switch (true) {
//     case leftSide && rightSide:
//       side = AnglesLocation.Both;
//       break;
//     case leftSide:
//       side = AnglesLocation.Left;
//       break;
//     default:
//       side = AnglesLocation.Right;
//   }

//   for (const { motion, region, reps } of params) {
//     apiParams.push({ motion, region, reps: Number(reps) });
//   }

//   return { side, params: apiParams };
// };

export const getDefaultTestsData = (): IGrouppedCreateOrderItems => {
  const defaultDuration = { duration: 10, durationType: DurationType.Seconds };
  const defaultFrequency = { frequency: FrequencyType.Weekly, frequencyReps: 1 };

  // set all tests initially to true and unselected
  // submit will be disabled because we require at least one selected test
  // on selection, validation is automatically triggered for particular test
  return {
    [TestType.Gait]: {
      selected: false,
      valid: true,
      tests: [],
    },
    [TestType.Balance]: {
      selected: false,
      valid: true,
      tests: [],
      ...defaultDuration,
      ...defaultFrequency,
    },
    // initialy valid, since it's hidden in first version
    [TestType.Angles]: { selected: false, valid: true, params: [] },
  };
};

const extractDurationAndFrequency = ({
  duration,
  durationType,
  subjectParam: { frequency, frequencyReps },
}: IOrderItem) => {
  // Convert Daily to Weekly for UI display
  if (frequency === FrequencyType.Daily) {
    return {
      duration,
      durationType,
      frequency: FrequencyType.Weekly,
      frequencyReps: frequencyReps * 7, // Convert daily to weekly
    };
  }

  return {
    duration,
    durationType,
    frequency,
    frequencyReps,
  };
};

const isCustomDuration = (duration: number): boolean => {
  const predefinedOptions = getTestDurationOptions();

  return predefinedOptions.every(({ value }) => {
    const currentDuration = Number(value.split("_")[0]);

    return duration !== currentDuration;
  });
};

export const getTestDataFromExistingOrder = ({
  orderItems,
}: IOrderDetails): IGrouppedCreateOrderItems => {
  const testsData: IGrouppedCreateOrderItems = getDefaultTestsData();

  const gaitOrderItems = orderItems.filter(
    ({ testId }) => testId === TestType.Gait
  );
  const balanceOrderItems = orderItems.filter(
    ({ testId }) => testId === TestType.Balance
  );

  if (gaitOrderItems.length) {
    const gaitTests: IGaitTestItem[] = gaitOrderItems.map((x) => {
      const durationAndFrequency = extractDurationAndFrequency(x);

      // If frequencyReps > 7, mark as Custom for UI display
      if (durationAndFrequency.frequencyReps > 7) {
        return {
          ...durationAndFrequency,
          frequency: FrequencyType.Custom,
          isCustom: isCustomDuration(x.duration),
        };
      }

      return {
        ...durationAndFrequency,
        isCustom: isCustomDuration(x.duration),
      };
    });

    testsData[TestType.Gait] = {
      selected: true,
      valid: true,
      tests: gaitTests,
    };
  }

  if (balanceOrderItems.length) {
    // duration and frequency are equal for all balance items
    const durationAndFrequency = extractDurationAndFrequency(
      balanceOrderItems[0]
    );

    // If frequencyReps > 7, mark as Custom for UI display
    if (durationAndFrequency.frequencyReps > 7) {
      durationAndFrequency.frequency = FrequencyType.Custom;
    }

    const balanceTests: IBalanceTestItem[] = balanceOrderItems.map(
      ({ eyes, feet }) => ({
        params: { eyes, feet },
      })
    );

    testsData[TestType.Balance] = {
      selected: true,
      valid: true,
      tests: balanceTests,
      ...durationAndFrequency,
    };
  }

  return testsData;
};

export const extractDurationAndType = (
  value: TestDurationValue
): { duration: number; durationType: DurationType } => {
  const [duration, durationType] = value.split("_").map((x) => Number(x));

  return {
    duration,
    durationType,
  };
};

export const getExpiringOrdersLabel = (
  expiringEnum: OrderExpiration
): string => {
  switch (expiringEnum) {
    case OrderExpiration.ExpiresToday:
      return "Orders - Expires today";
    case OrderExpiration.ExpiresInOneWeek:
      return "Orders - Expires in 1 week";
    case OrderExpiration.ExpiresInTwoWeeks:
      return "Orders - Expires in 2 weeks";
    case OrderExpiration.RecentlyExpired:
      return "Orders - Recently expired";
  }
};

export const getPayerTypeLabel = (value: PayerType) => {
  switch (value) {
    case PayerType.Commercial:
      return "Commercial";
    case PayerType.Medicare:
      return "Medicare";
    case PayerType.Medicaid:
      return "Medicaid";
    case PayerType.PersonalInjury:
      return "Personal Injury";
    case PayerType.SelfPay:
      return "Self Pay";
    case PayerType.WorkersCompensation:
        return "Workers Compensation";
      case PayerType.MedicareAdvantage:
        return "Medicare Advantage";
    }
};

export const getOrderTypeLabel = (value: Apps) => {
  switch (value) {
    case Apps.AtHome:
      return "LAB AT HOME";
    case Apps.Remote:
      return "LAB REMOTE";
  }
};

// what voiding does to the order's charges, one line each; empty when it has none
export const getVoidChargeLines = (info: IOrderVoidInfo): string[] => {
  const lines: string[] = [];
  const invoices = info.invoicedMonths.length;
  if (invoices)
    lines.push(
      `${invoices} ${invoices === 1 ? "invoice" : "invoices"} already generated from ${info.invoicedMonths.join(", ")}`
    );
  if (info.uninvoicedCount)
    lines.push(
      `${info.uninvoicedMonths.join(", ")} will not be invoiced`
    );
  return lines;
};
